import React, { useRef, useState } from 'react';
import { Undo2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BarcodeInput from '@/components/BarcodeInput';
import apiClient from '@/services/apiClient';

const typeLabel = (t) => (t === 'job_worker' ? 'Job Worker' : t === 'internal_transfer' ? 'Internal Transfer' : t || '-');

const ScannedRow = ({ b, onRemove }) => (
  <div className="flex items-start justify-between gap-3 border-b border-[#F0EBDF] py-2 last:border-0" data-testid={`otr-scanned-${b.bundle_barcode}`}>
    <div className="text-sm">
      <div className="font-mono font-semibold text-[#1A1F2E]">{b.bundle_barcode}</div>
      <div className="text-[#5A5F6E]">
        Doc {b.document_barcode || '-'} · {typeLabel(b.transfer_type)} → {b.destination || '-'} · Vehicle {b.original_vehicle_no || '-'}
      </div>
    </div>
    <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(b.bundle_barcode)} data-testid={`otr-remove-${b.bundle_barcode}`}>
      <X className="w-4 h-4" />
    </Button>
  </div>
);

export const RecordReturnPanel = ({ staffId, onRecorded }) => {
  const scanRef = useRef(null);
  const [code, setCode] = useState('');
  const [scanned, setScanned] = useState([]);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const handleScan = async (value) => {
    const c = value.trim();
    setCode('');
    if (!c) return;
    if (scanned.some((b) => b.bundle_barcode === c)) { setError(`Bundle ${c} already scanned`); return; }
    try {
      const r = await apiClient.get('/outward/returns/scan-bundle', { params: { code: c } });
      if (!r.data.valid) { setError(r.data.message); toast.error(r.data.message); return; }
      setError('');
      setScanned((prev) => [r.data.bundle, ...prev]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Scan failed');
    } finally {
      scanRef.current?.focus();
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post('/outward/returns/record', {
        staff_id: staffId, bundle_barcodes: scanned.map((b) => b.bundle_barcode), return_reason: reason,
      });
      toast.success(r.data.message);
      setScanned([]); setReason(''); setError('');
      onRecorded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record return');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm space-y-5" data-testid="otr-record-panel">
      <BarcodeInput ref={scanRef} label="Scan returned bundle" value={code} onChange={setCode} onScan={handleScan}
        placeholder="Scan bundle barcode..." autoFocus error={error} data-testid="otr-bundle-scan-input" />
      <div>
        <div className="text-sm font-medium text-[#1A1F2E] mb-1" data-testid="otr-scanned-count">Scanned: {scanned.length}</div>
        {scanned.length === 0
          ? <p className="text-sm text-[#8A8F9E]">No bundles scanned yet.</p>
          : <div className="rounded-md border border-[#E5DFD0] px-3">{scanned.map((b) => (
            <ScannedRow key={b.bundle_barcode} b={b} onRemove={(bc) => setScanned((p) => p.filter((x) => x.bundle_barcode !== bc))} />))}</div>}
      </div>
      <div className="space-y-2">
        <Label>Return reason (optional)</Label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Vehicle breakdown" data-testid="otr-reason-input" />
      </div>
      <Button onClick={submit} disabled={busy || scanned.length === 0} className="w-full h-12 bg-[#B8374A] hover:bg-[#9c2e3e]" data-testid="otr-record-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
        Record Return ({scanned.length})
      </Button>
    </div>
  );
};

export default RecordReturnPanel;
